import {parseMoney} from '@sollapay/utils';

import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {GeneralSaleTransactionType, PayinType, PayoutType, TimeLength} from '@sollapay/enums';
import type {TrustScopeDetailsDTO, TrustScopeUpsertPayload} from '@sollapay/types';

export interface TrustScopeFormValues {
  timeLength: TimeLength | '';
  volume: string;
  payoutTypes: PayoutType[];
  payoutOtherText: string;
  payinTypes: PayinType[];
  payinOtherText: string;
  transactionType: GeneralSaleTransactionType | '';
}

export const EMPTY_TRUST_SCOPE_FORM_VALUES: TrustScopeFormValues = {
  timeLength: '',
  volume: '',
  payoutTypes: [],
  payoutOtherText: '',
  payinTypes: [],
  payinOtherText: '',
  transactionType: ''
};

/**
 * `strategy` filters `payoutTypes` down to what the deal type currently offers — a legacy row
 * saved under a different deal type (or before a payout type was dropped from one) shows an
 * honest list rather than a stale value the lawyer can no longer see or remove.
 */
export const toTrustScopeFormValues = (
  scope: TrustScopeDetailsDTO | null | undefined,
  strategy: TrustTypeStrategy
): TrustScopeFormValues => {
  if (!scope) {
    return EMPTY_TRUST_SCOPE_FORM_VALUES;
  }

  return {
    timeLength: scope.timeLength ?? '',
    volume: scope.volume
      ? parseMoney(scope.volume)
          .toString()
          .replace(/\.\d+$/, '')
      : '',
    payoutTypes: strategy.resolvePayoutTypes(scope.payoutTypes),
    payoutOtherText: scope.payoutOtherText ?? '',
    payinTypes: scope.payinTypes ?? [],
    payinOtherText: scope.payinOtherText ?? '',
    transactionType: scope.transactionType ?? ''
  };
};

export const toTrustScopeDetailsDTO = (values: TrustScopeFormValues): TrustScopeUpsertPayload => ({
  timeLength: (values.timeLength as TimeLength) || null,
  volume: values.volume.trim() || null,
  payoutTypes: values.payoutTypes.length > 0 ? values.payoutTypes : [],
  payoutOtherText: values.payoutOtherText.trim() || '',
  payinTypes: values.payinTypes.length > 0 ? values.payinTypes : [],
  payinOtherText: values.payinOtherText.trim() || '',
  transactionType: values.transactionType || null
});
